import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Card, CardBody, Col, Row } from 'reactstrap';
import Content from '../../../layout/Content';
import Table from '../../../common/Table';
import TableToolbar from '../../../common/TableToolbar';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { BANK_RECONCILIATION_DATE_COLUMNS } from '../../../../config/columns';
import {
  BANK_RECONCILIATION_STATUS,
  DEFAULT_PAGE_SIZE,
} from '../../../../config/constants';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import { NEW_DEPOSIT_MIGRATION_PATH } from '../../../../config/paths';
import { tzNormalizeDate } from '../../../../utils/date';
import { enumToSelectOptions } from '../../../../utils/enum';
import {
  getBankReconciliations,
  clearBankReconciliations,
} from '../../../../actions';
import BankReconciliationLiquidationsTable from './BankReconciliationLiquidationsTable';
import BankReconciliationCorporateCoverageTable from './BankReconciliationCorporateCoverageTable';
import BankReconciliationBankSummaryTable from './BankReconciliationBankSummaryTable';
import BankReconciliationLiquidationCoverageTable from './BankReconciliationLiquidationCoverageTable';
import BankReconciliationSearchForm from './BankReconciliationSearchForm';

const DEFAULT_FORM_VALUES = {
  from: new Date(),
  to: new Date(),
  bank: null,
  agency: null,
  status: enumToSelectOptions(BANK_RECONCILIATION_STATUS),
};

const toQuery = ({ from, to, bank, agency, status }) => ({
  from: from ? tzNormalizeDate({ date: from, format: INT_DATE_FORMAT }) : null,
  to: to ? tzNormalizeDate({ date: to, format: INT_DATE_FORMAT }) : null,
  bankId: bank ? bank.value : null,
  agencyId: agency ? agency.value : null,
  status: status ? status.map(({ value }) => value) : [],
});

const BankReconciliation = ({
  breadcrumbs,
  dates,
  loading,
  dispatchGetBankReconciliations,
  dispatchClearBankReconciliations,
}) => {
  const [appliedQuery, setAppliedQuery] = useState(
    toQuery(DEFAULT_FORM_VALUES),
  );

  useEffect(() => () => dispatchClearBankReconciliations(), []);

  const onSubmit = (formValues) => {
    const query = toQuery(formValues);

    setAppliedQuery(query);
    dispatchGetBankReconciliations({
      ...query,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    });
  };

  const content = dates.get('content') || [];
  const pages = dates.get('totalPages') || null;
  const defaultPageSize = dates.get('size') || DEFAULT_PAGE_SIZE;

  const renderDateDetail = ({ original }) => (
    <>
      <BankReconciliationLiquidationsTable
        date={original.liquidationDate}
        dateTotals={original}
        filters={appliedQuery}
      />
      <BankReconciliationCorporateCoverageTable
        date={original.liquidationDate}
      />
    </>
  );

  const toolbar = (
    <TableToolbar
      buttonPath={NEW_DEPOSIT_MIGRATION_PATH}
      buttonText="Cargar estado de cuenta"
    />
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title="Conciliación bancaria por liquidación"
      content={
        <>
          <Row>
            <Col lg={7}>
              <BankReconciliationBankSummaryTable filters={appliedQuery} />
            </Col>
            <Col lg={5}>
              <Card className="shadow-sm border-0 mb-4">
                <CardBody>
                  <BankReconciliationSearchForm
                    onSubmit={onSubmit}
                    initialValues={DEFAULT_FORM_VALUES}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <BankReconciliationLiquidationCoverageTable />

          <Card className="shadow-sm border-0 mb-4">
            <div className="px-3 py-2 border-bottom bg-white">
              <span className="font-weight-medium">
                Detalle por fecha de liquidación: liquidaciones, corporativos y
                sus depósitos
              </span>
            </div>
            <Table
              columns={BANK_RECONCILIATION_DATE_COLUMNS}
              data={content}
              pages={pages}
              defaultPageSize={defaultPageSize}
              loading={loading}
              fetchData={dispatchGetBankReconciliations}
              params={appliedQuery}
              rowComponent={renderDateDetail}
            />
          </Card>
        </>
      }
    />
  );
};

BankReconciliation.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dates: PropTypes.instanceOf(Immutable.Map),
  loading: PropTypes.bool,
  dispatchGetBankReconciliations: PropTypes.func.isRequired,
  dispatchClearBankReconciliations: PropTypes.func.isRequired,
};

BankReconciliation.defaultProps = {
  dates: null,
  loading: false,
};

const mapStateToProps = ({ SalesUnit }) => ({
  breadcrumbs: [
    ...SalesUnit.UnitHome.get('breadcrumbs'),
    { text: 'Conciliación bancaria', href: '' },
  ],
  dates: SalesUnit.BankReconciliation.getIn(['all', 'content']),
  loading: SalesUnit.BankReconciliation.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchGetBankReconciliations: getBankReconciliations,
  dispatchClearBankReconciliations: clearBankReconciliations,
};

export { BankReconciliation };
export default connect(mapStateToProps, mapDispatchToProps)(BankReconciliation);
